import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'

export default function MountSabinyoClimb() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Mount Sabinyo Volcano Climb
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Stand in three countries at once atop the "Old Man's Teeth"
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(76 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-flag" className="w-4 h-4 mr-2" />
                3-Country Summit
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-mountain" className="w-4 h-4 mr-2" />
                3,669m Elevation
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Climb
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Climb to Where Three Nations Meet
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Mount Sabinyo earns its local name "Old Man's Teeth" from the jagged peaks that crown its summit ridge, resembling worn molars jutting skyward. But this extinct volcano's real claim to fame is geographical: its summit marks the precise point where Uganda, Rwanda, and the Democratic Republic of Congo converge. Standing atop Sabinyo's highest peak, you can quite literally place one foot in each of three countries simultaneously—a surreal moment that makes the challenging eight-hour climb worthwhile.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  At 3,669 meters elevation, this is not the tallest of the Virunga volcanoes, but the steep, exposed final ascent involving ladders and rope assists makes it arguably the most technically demanding one-day hike in the region. If you seek adventure that combines physical challenge, stunning Afromontane scenery, and a uniquely border-straddling experience, Sabinyo delivers unforgettably.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The real adventure begins when you reach the rocky summit ridge. Here the trail transforms from steep hiking into genuine scrambling—you will use fixed ladders bolted to near-vertical rock faces, haul yourself up using permanently installed ropes, and navigate narrow passages where exposure is significant. The sense of accomplishment upon reaching the top is immense, and your guides provide assistance and encouragement throughout.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Climb Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-flag', title: 'Three-Country Summit', description: 'Stand simultaneously in Uganda, Rwanda, and DRC' },
                  { icon: 'fa-mountain', title: 'Technical Challenge', description: 'Ladders, ropes, and exposed scrambling' },
                  { icon: 'fa-eye', title: 'Panoramic Views', description: 'Overlook other volcanoes and vast landscapes' },
                  { icon: 'fa-leaf', title: 'Vegetation Zones', description: 'Bamboo, hagenia forest, and afro-alpine heath' },
                  { icon: 'fa-users', title: 'Golden Monkeys', description: 'Chance to see monkeys in the lower bamboo zone' },
                  { icon: 'fa-award', title: 'Summit Certificate', description: 'Official documentation of your achievement' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Climb Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences
                  and fitness level. Timing depends on weather and group pace.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '08:00', title: 'Park Briefing', desc: 'Meet rangers at Mgahinga headquarters for safety orientation and porter hire.' },
                  { time: '08:30', title: 'Bamboo Zone', desc: 'Begin climb through bamboo forest. Watch for golden monkeys and bushbucks.' },
                  { time: '10:00', title: 'Hagenia Forest', desc: 'Enter moss-draped woodland. Steep climbing with short breaks.' },
                  { time: '11:30', title: 'Heathland Zone', desc: 'Vegetation thins to giant lobelias. Views open up as altitude increases.' },
                  { time: '13:00', title: 'The Ladders', desc: 'Navigate the famous wooden ladders and steep ridges to the peaks.' },
                  { time: '14:00', title: 'Summit Success!', desc: 'Reach Peak 3 (3,669m). Photos in 3 countries. Packed lunch with a view.' },
                  { time: '16:30', title: 'Return to Base', desc: 'Descend via the same route. Receive your summit certificate.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Essential Gear
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof hiking boots (essential)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Rain jacket & pants</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Warm layers (fleece/down)</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-bag-shopping" className="w-6 h-6" />
                    Equipment
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Gardening gloves (for ladders)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>2-3 liters of water</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Energy snacks/packed lunch</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                {[
                  { q: 'Do I need climbing experience?', a: 'No technical skills needed, but you must be comfortable with heights and ladders.' },
                  { q: 'How fit do I need to be?', a: 'Very fit. It is a steep, strenuous 8-hour hike with 1,400m elevation gain.' },
                  { q: 'Should I hire a porter?', a: 'Yes! A porter carries your bag and helps you on steep sections. Highly recommended and supports local communities.' },
                  { q: 'What if I can\'t make it?', a: 'You can turn back at any peak. Reaching Peak 1 is still a great achievement!' }
                ].map((faq, index) => (
                  <div key={index} className="border-l-4 border-safari-gold pl-6 py-4">
                    <h3 className="text-xl font-bold text-safari-green mb-2">{faq.q}</h3>
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Mgahinga Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/golden-monkey-tracking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800"
                      alt="Golden Monkey Tracking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Golden Monkey Tracking</h3>
                    <p className="text-gray-700 mb-4">Track rare primates in the bamboo forest.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/mount-gahinga-climb" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=800"
                      alt="Mount Gahinga"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Gahinga Climb</h3>
                    <p className="text-gray-700 mb-4">A scenic, easier volcano hike.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 7-9 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Dry seasons</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Strenuous</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-mountain" className="w-6 h-6 text-safari-green" />
                    <span><strong>Elevation:</strong> 3,669m</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Climb
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Climbing permit",
                    "Park entrance fees",
                    "Armed ranger guides",
                    "Safety equipment (ropes)",
                    "Summit certificate",
                    "Emergency support"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Porter hire (recommended)",
                    "Packed lunch & water",
                    "Transport to park",
                    "Hiking boots/gear",
                    "Tips"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> High level required.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Vertigo:</strong> Not for those with fear of heights.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Mount Sabinyo Climb"
      />
    </div>
  )
}
